/**
 * tr-product-form — kernel transactionnel du produit (contrat v2).
 *
 * Rôle : collecter ce qui doit être ajouté au panier, puis l'envoyer en un
 * seul appel `items[]` — via le moteur panier (`window.Traction.cart`) quand
 * il est présent (POST /cart/add avec `sections` : tiroir + bulle rafraîchis
 * dans la même requête), sinon en direct.
 *
 * Fournisseurs (descendants exposant `getSelectedItems()`, marqués
 * `data-provider`) :
 *   - `primary` (quantity-breaks, bundle-fixed, bundle-builder) : remplace le
 *     variant/quantité courants. S'il y en a plusieurs, le premier visible gagne.
 *   - `addon` (cross-sell, gift) : ajoutés à la liste.
 *   - aucun fournisseur → `[{ id, quantity, selling_plan? }]`.
 *
 * Chaque fournisseur peut aussi exposer :
 *   - `selectedTotal` (centimes) → total affiché sur le bouton ;
 *   - `validationMessage` (string vide = valide) → bloque l'envoi ;
 *   - `updateVariant(variant)` → appelé à chaque changement de variante ;
 *   - `updateSellingPlan(planId, unitPrice)` → appelé à chaque changement de
 *     plan d'achat ET après chaque `updateVariant` ; `unitPrice` est le prix
 *     unitaire effectif en centimes (allocation du plan, sinon prix variante).
 *
 * Événements :
 *   émis   tr:variant-change (document) { productId, variantId, variant, available, price, compareAtPrice, featuredMediaId }
 *   émis   tr:cart-add      (document) { items, cart, source: 'product-form' } (voie directe ; via le moteur, c'est lui qui l'émet avec `sections`)
 *   émis   tr:cart-error    (document) { message, status, source, cart? } (voie directe ; le moteur émet le sien)
 *   émis   tr:cart-open     (document)
 *   émis   tr:form-change   (élément)  { items, total, quantity, multi }
 *   écoute tr:variant-selected (picker), tr:selection-change, tr:quantity-change (bulle)
 *
 * Tous les prix sont formatés via `window.Traction.formatMoney` (défini ici si absent).
 */

const MONEY_FORMATS = {
  amount: [2, ',', '.'],
  amount_no_decimals: [0, ',', ''],
  amount_with_comma_separator: [2, '.', ','],
  amount_no_decimals_with_comma_separator: [0, '.', ''],
  amount_with_space_separator: [2, ' ', ','],
  amount_no_decimals_with_space_separator: [0, ' ', ''],
  amount_with_period_and_space_separator: [2, ' ', '.'],
  amount_with_apostrophe_separator: [2, "'", '.'],
};

/** Formate des centimes selon `shop.money_format` (placeholders Shopify). */
function formatMoney(cents, format) {
  const template = format ?? window.Traction?.moneyFormat ?? '{{amount}}';
  const value = Number(cents) || 0;

  return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    const [decimals, thousands, decimal] = MONEY_FORMATS[key] ?? MONEY_FORMATS.amount;
    const fixed = (value / 100).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    if (fraction === undefined) return grouped;
    if (/^0+$/.test(fraction)) return grouped; /* aligné sur tr-money : « ,00 » tombe */
    return `${grouped}${decimal}${fraction}`;
  });
}

window.Traction = window.Traction ?? {};
if (typeof window.Traction.formatMoney !== 'function') {
  window.Traction.formatMoney = formatMoney;
}

/** Route Ajax locale-aware, suffixée `.js`. */
function cartRoute(name, fallback) {
  const route = window.Traction?.routes?.[name] ?? window.routes?.[name] ?? fallback;
  return route.endsWith('.js') ? route : `${route}.js`;
}

/** Lit un corps JSON sans jeter (réponses vides ou HTML d'erreur). */
async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function isVisible(node) {
  if (typeof node.checkVisibility === 'function') return node.checkVisibility();
  return node.offsetParent !== null;
}

/**
 * Message lisible d'une réponse d'erreur Ajax. Les erreurs de carte cadeau
 * (bénéficiaire) arrivent avec `description` en objet `{ email: ['n’est pas valide'] }` ;
 * `message` porte alors la phrase complète (« Validation échouée : E-mail n’est pas valide »).
 */
function errorText(data, fallback) {
  if (typeof data.description === 'string' && data.description !== '') return data.description;
  if (typeof data.message === 'string' && data.message !== '') return data.message;

  const detail = data.errors ?? data.description;
  if (detail !== null && typeof detail === 'object') {
    const text = Object.values(detail).flat().join(' ');
    if (text !== '') return text;
  }

  return data.message ?? fallback;
}

/** Signature stable d'une ligne : deux lignes identiques sont fusionnables. */
function itemSignature(item) {
  const properties = item.properties ?? null;
  const flat =
    properties === null
      ? ''
      : Object.keys(properties)
          .sort()
          .map((key) => `${key}=${properties[key]}`)
          .join('&');

  return `${item.id}|${item.selling_plan ?? ''}|${flat}`;
}

/**
 * Fusionne les lignes strictement identiques (même variante, même plan d'achat,
 * mêmes propriétés) en additionnant les quantités — 04·R3.
 *
 * Sans ça, un palier et un cross-sell qui visent la MÊME variante partent en
 * deux entrées `items[]` : Shopify n'en fait qu'une ligne de panier, mais les
 * Web Pixels (`product_added_to_cart`) et les apps d'analytics comptent deux
 * ajouts. Les lignes dont les propriétés diffèrent restent séparées : Shopify
 * les traite lui aussi comme deux lignes distinctes.
 */
function mergeItems(items) {
  const merged = new Map();

  for (const item of items) {
    const key = itemSignature(item);
    const existing = merged.get(key);

    if (existing === undefined) {
      merged.set(key, item);
      continue;
    }

    existing.quantity += item.quantity;
  }

  return Array.from(merged.values());
}

class TrProductForm extends HTMLElement {
  #form = null;
  #button = null;
  #busy = false;
  #variant = null;
  #inventory = null;

  connectedCallback() {
    this.#form = this.querySelector('[data-ref="form"]');
    this.#button = this.querySelector('[data-ref="addToCart"]');

    if (this.#form === null) return;

    this.#form.addEventListener('submit', this.#onSubmit);
    this.addEventListener('tr:selection-change', this.#onSelectionChange);
    this.addEventListener('tr:quantity-change', this.#onSelectionChange);
    this.addEventListener('tr:variant-selected', this.#onVariantSelected);
    this.addEventListener('change', this.#onChange);

    this.#readCurrentVariant();
    this.#placeSellingPlans();
    this.#propagatePlan();
    this.#refresh();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('tr:selection-change', this.#onSelectionChange);
    this.removeEventListener('tr:quantity-change', this.#onSelectionChange);
    this.removeEventListener('tr:variant-selected', this.#onVariantSelected);
    this.removeEventListener('change', this.#onChange);
  }

  /* ---------- public ---------- */

  /** Identifiant de la variante courante. */
  get variantId() {
    return Number(this.dataset.variantId) || 0;
  }

  /** Prix unitaire courant (plan d'achat inclus), en centimes. */
  get unitPrice() {
    const planId = this.sellingPlanId;

    if (planId !== null && this.#variant?.selling_plan_allocations) {
      const allocation = this.#variant.selling_plan_allocations.find(
        (entry) => String(entry.selling_plan_id) === planId
      );
      if (allocation !== undefined) return Number(allocation.price) || 0;
    }

    return Number(this.dataset.variantPrice) || 0;
  }

  /** Plan d'achat sélectionné (string) ou null pour l'achat unique. */
  get sellingPlanId() {
    const checked = this.querySelector('[data-ref="planChoice"]:checked');
    const value = checked?.value ?? '';
    return value === '' ? null : value;
  }

  /** Quantité du sélecteur natif (1 par défaut). */
  get quantity() {
    const field = this.querySelector('[data-ref="quantityField"]');
    return Math.max(1, Number(field?.value) || 1);
  }

  /** Total courant affiché (centimes). */
  get currentTotal() {
    return this.#computeTotal();
  }

  /**
   * Quantité du produit principal (hors additifs) : celle du fournisseur
   * primaire s'il y en a un, sinon le sélecteur natif. Utilisée par `gift`.
   */
  get mainQuantity() {
    const primary = this.#primaryProvider();
    if (primary === null) return this.quantity;

    const items = primary.getSelectedItems();
    if (Array.isArray(items) === false || items.length === 0) return 0;

    const own = items.filter((item) => Number(item.id) === this.variantId);
    const source = own.length > 0 ? own : [items[0]];

    return source.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  /**
   * Ce qui part au panier — agrégation des fournisseurs.
   * Les clés inconnues de l'Ajax API sont retirées, puis les lignes
   * strictement identiques sont fusionnées (`mergeItems`, 04·R3).
   */
  getItems() {
    const primary = this.#primaryProvider();
    const items = [];

    if (primary !== null) {
      const provided = primary.getSelectedItems();
      if (Array.isArray(provided)) items.push(...provided);

      /* Primaire vide (kit tout décoché…) : rien ne part, pas même les
         additifs — un cadeau/upsell ne doit jamais partir seul au panier. */
      if (items.length === 0) return [];
    } else if (this.variantId !== 0) {
      items.push({ id: this.variantId, quantity: this.quantity });
    }

    for (const addon of this.#addonProviders()) {
      const provided = addon.getSelectedItems();
      if (Array.isArray(provided)) items.push(...provided);
    }

    const planId = this.sellingPlanId;
    const preorder = this.#preorderState();
    const fieldProperties = this.#customFieldProperties();

    const lines = items
      .filter((item) => Number(item?.id) > 0 && Number(item?.quantity) > 0)
      .map((item) => {
        const clean = { id: Number(item.id), quantity: Number(item.quantity) };
        const properties = { ...(item.properties ?? {}) };

        /* Champs personnalisés (blocks/custom-field) : propriétés du produit
           principal uniquement (jamais les additifs). */
        if (clean.id === this.variantId) Object.assign(properties, fieldProperties);

        /* Précommande honnête : propriété visible sur les lignes du produit
           principal uniquement (jamais sur les additifs). */
        if (preorder.active && clean.id === this.variantId && preorder.key !== '') {
          properties[preorder.key] = preorder.message;
        }

        if (Object.keys(properties).length > 0) clean.properties = properties;

        if (item.selling_plan) {
          clean.selling_plan = item.selling_plan;
        } else if (planId !== null && clean.id === this.variantId) {
          clean.selling_plan = planId;
        }

        return clean;
      });

    return mergeItems(lines);
  }

  /**
   * Line item properties saisies dans le formulaire (blocks/custom-field) :
   * inputs `name="properties[Clé]"` — cases décochées et champs vides ignorés.
   */
  #customFieldProperties() {
    const properties = {};

    for (const field of this.querySelectorAll('[name^="properties["]')) {
      const match = /^properties\[(.+)\]$/.exec(field.getAttribute('name') ?? '');
      if (match === null) continue;

      if (field.type === 'checkbox' && field.checked === false) continue;
      if (field.disabled) continue;

      const value = String(field.value ?? '').trim();
      if (value === '') continue;

      properties[match[1]] = value;
    }

    return properties;
  }

  /** Met à jour la variante courante et propage à tous les descendants. */
  setVariant(variant) {
    if (variant === null || variant === undefined) return;

    this.#variant = variant;
    this.dataset.variantId = String(variant.id);
    this.dataset.variantPrice = String(variant.price);
    this.dataset.available = String(variant.available);

    const input = this.querySelector('[data-ref="variantInput"]');
    if (input !== null) {
      input.value = variant.id;
      input.disabled = variant.available === false;
    }

    for (const node of this.querySelectorAll('[data-provider]')) {
      if (typeof node.updateVariant === 'function') node.updateVariant(variant);
    }

    this.#propagatePlan();
    this.#updatePlanPrices();
    this.#refresh();

    document.dispatchEvent(
      new CustomEvent('tr:variant-change', {
        bubbles: true,
        detail: {
          productId: Number(this.dataset.productId) || null,
          variantId: variant.id,
          variant,
          available: variant.available,
          price: variant.price,
          compareAtPrice: variant.compare_at_price ?? null,
          featuredMediaId: variant.featured_media?.id ?? null,
        },
      })
    );
  }

  /* ---------- fournisseurs ---------- */

  #providers(kind) {
    const nodes = this.querySelectorAll(`[data-provider="${kind}"]`);
    return Array.from(nodes).filter(
      (node) => typeof node.getSelectedItems === 'function' && isVisible(node)
    );
  }

  #primaryProvider() {
    return this.#providers('primary')[0] ?? null;
  }

  #addonProviders() {
    return this.#providers('addon');
  }

  /**
   * Propage le plan d'achat courant à TOUS les fournisseurs (même masqués :
   * un bloc caché par `applies_to` doit pouvoir se ré-afficher).
   * `unitPrice` = prix effectif (allocation du plan, sinon prix variante).
   */
  #propagatePlan() {
    const planId = this.sellingPlanId;
    const unitPrice = this.unitPrice;

    for (const node of this.querySelectorAll('[data-provider]')) {
      if (typeof node.updateSellingPlan === 'function') node.updateSellingPlan(planId, unitPrice);
    }
  }

  /* ---------- soumission ---------- */

  #onSubmit = async (event) => {
    event.preventDefault();

    if (this.#busy) return;

    /* Champs personnalisés `required` (07·R5) : le formulaire est novalidate,
       la validation native passe par reportValidity() avant toute collecte. */
    const form = this.querySelector('[data-ref="form"]');
    if (form !== null && typeof form.reportValidity === 'function' && form.reportValidity() === false) return;

    const blocking = this.#validationMessage();
    if (blocking !== '') {
      this.#showError(blocking);
      return;
    }

    const items = this.getItems();

    if (items.length === 0) {
      this.#showError(this.dataset.stringSelect ?? '');
      return;
    }

    this.#setBusy(true);
    this.#clearError();
    this.#setStatus('');

    try {
      const engine = window.Traction?.cart;

      /* Voie moteur : un seul POST /cart/add avec `sections` (tiroir + bulle
         re-rendus dans la même réponse) ; le moteur émet tr:cart-add/-error. */
      if (engine !== undefined && typeof engine.add === 'function') {
        const result = await engine.add(items, { source: 'product-form' });

        if (result?.ok === false) {
          this.#showError(result.message ?? this.dataset.stringError ?? '');
          return;
        }

        this.#setStatus(this.dataset.stringAdded ?? '');
        this.#afterAdd();
        return;
      }

      /* Voie directe (aucun moteur panier sur la page). */
      const response = await fetch(cartRoute('cart_add_url', '/cart/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const payload = await readJson(response);

      if (response.ok === false) {
        const message = errorText(payload, this.dataset.stringError ?? '');
        const status = Number(payload.status) || response.status;

        /* 422 = succès partiel possible (stock) : on relit le panier. */
        const cart = status === 422 ? await this.#fetchCart() : null;

        this.#showError(message);
        this.#emitError(message, status, cart);
        return;
      }

      const cart = await this.#fetchCart();

      document.dispatchEvent(
        new CustomEvent('tr:cart-add', {
          bubbles: true,
          detail: { items, cart, source: 'product-form' },
        })
      );

      this.#setStatus(this.dataset.stringAdded ?? '');
      this.#afterAdd();
    } catch (error) {
      const message = this.dataset.stringNetwork ?? String(error);
      this.#showError(message);
      this.#emitError(message, 0, null);
    } finally {
      this.#setBusy(false);
    }
  };

  #emitError(message, status, cart) {
    document.dispatchEvent(
      new CustomEvent('tr:cart-error', {
        bubbles: true,
        detail: { message, status, source: 'product-form', cart },
      })
    );
  }

  async #fetchCart() {
    try {
      const response = await fetch(cartRoute('cart_url', '/cart'), {
        headers: { Accept: 'application/json' },
      });
      return response.ok ? await readJson(response) : null;
    } catch {
      return null;
    }
  }

  #afterAdd() {
    const action = this.dataset.cartAction;
    const cartUrl = window.Traction?.routes?.cart_url ?? window.routes?.cart_url ?? '/cart';

    if (action === 'cart') {
      window.location.assign(cartUrl);
      return;
    }

    if (action === 'drawer') {
      if (window.Traction?.cartType === 'page') {
        window.location.assign(cartUrl);
        return;
      }

      document.dispatchEvent(new CustomEvent('tr:cart-open', { bubbles: true }));
    }
  }

  /* ---------- réactions ---------- */

  #onSelectionChange = () => {
    this.#refresh();
  };

  #onVariantSelected = (event) => {
    this.setVariant(event.detail?.variant);
  };

  #onChange = (event) => {
    if (event.target.matches('[data-ref="planChoice"]') === false) return;

    const input = this.querySelector('[data-ref="sellingPlanInput"]');
    const planId = this.sellingPlanId;

    if (input !== null) {
      input.value = planId ?? '';
      input.disabled = planId === null;
    }

    this.#propagatePlan();
    this.#refresh();
  };

  /**
   * Recalcule le total, synchronise les champs natifs et l'état du bouton.
   * Appelé à chaque changement de sélection, de quantité, de variante ou de plan.
   */
  #refresh() {
    const items = this.getItems();
    const total = this.#computeTotal();
    const available = this.dataset.available !== 'false';
    const single = items.length === 1 && items[0].id === this.variantId;

    /* Sans JS le formulaire natif n'envoie que id + quantity : on les garde alignés. */
    const quantityInput = this.querySelector('[data-ref="quantityInput"]');
    if (quantityInput !== null) {
      quantityInput.value = String(single ? items[0].quantity : this.quantity);
    }

    /* Paiement accéléré : uniquement pour un achat simple (un seul article). */
    const dynamic = this.querySelector('[data-ref="dynamicCheckout"]');
    if (dynamic !== null) dynamic.hidden = single === false;

    this.#updateButton(available, total);

    this.dispatchEvent(
      new CustomEvent('tr:form-change', {
        bubbles: false,
        detail: { items, total, quantity: single ? items[0].quantity : this.quantity, multi: single === false },
      })
    );
  }

  #computeTotal() {
    const primary = this.#primaryProvider();
    const base =
      primary !== null && typeof primary.selectedTotal === 'number'
        ? primary.selectedTotal
        : this.unitPrice * this.quantity;

    return this.#addonProviders().reduce(
      (sum, addon) => sum + (typeof addon.selectedTotal === 'number' ? addon.selectedTotal : 0),
      base
    );
  }

  #validationMessage() {
    const primary = this.#primaryProvider();
    const message = primary?.validationMessage;
    return typeof message === 'string' ? message : '';
  }

  #updateButton(available, total) {
    const preorder = this.#preorderState();
    this.#togglePreorderUi(preorder, available);

    if (this.#button === null) return;

    const label = this.#button.querySelector('[data-ref="addToCartLabel"]');
    const price = this.#button.querySelector('[data-ref="addToCartPrice"]');

    this.#button.disabled = available === false || this.#busy;

    if (label !== null) {
      if (available === false) {
        label.textContent = this.dataset.stringSoldOut ?? label.dataset.soldOutLabel ?? '';
      } else if (preorder.active && label.dataset.preorderLabel !== undefined) {
        label.textContent = label.dataset.preorderLabel;
      } else if (label.dataset.defaultLabel !== undefined) {
        label.textContent = label.dataset.defaultLabel;
      }
    }

    if (price !== null) price.textContent = window.Traction.formatMoney(total);
  }

  #updatePlanPrices() {
    if (this.#variant === null) return;

    for (const node of this.querySelectorAll('[data-ref="planPrice"]')) {
      const planId = node.dataset.planId ?? '';
      const allocation = this.#variant.selling_plan_allocations?.find(
        (entry) => String(entry.selling_plan_id) === planId
      );
      const price = planId === '' ? this.#variant.price : allocation?.price ?? this.#variant.price;
      node.textContent = window.Traction.formatMoney(price);
    }
  }

  /** Variante courante embarquée par le Liquid (allocations de plans incluses). */
  #readCurrentVariant() {
    const data = this.querySelector('[data-ref="currentVariant"]');
    if (data === null) return;

    try {
      const variant = JSON.parse(data.textContent);
      if (variant !== null && typeof variant === 'object') this.#variant = variant;
    } catch {
      /* la donnée arrivera au premier tr:variant-selected */
    }
  }

  /** Déplace le choix de plan juste avant le bouton d'ajout si demandé. */
  #placeSellingPlans() {
    const plans = this.querySelector('[data-ref="sellingPlans"]');
    if (plans === null || plans.dataset.position !== 'before_button' || this.#button === null) return;

    /* Le bouton statique vit après la zone de blocs ; sinon (bouton posé dans
       un bloc de la zone), on vise l'enfant de la zone qui le contient. */
    const body = this.querySelector('[data-ref="formBody"]');
    const root =
      this.querySelector('[data-ref="addToCartBlock"]') ??
      Array.from(body?.children ?? []).find((child) => child.contains(this.#button));

    if (root !== undefined && root !== null) root.before(plans);
  }

  /* ---------- précommande ---------- */

  /**
   * État précommande courant, depuis la configuration du bloc bouton
   * (`data-preorder*`) et l'inventaire embarqué (`data-ref="inventoryData"`).
   * Jamais de faux stock : ne s'active que si la variante reste achetable.
   */
  #preorderState() {
    const inactive = { active: false, key: '', message: '' };
    const root = this.querySelector('[data-ref="addToCartBlock"]');

    if (root === null || root.dataset.preorder !== 'true') return inactive;
    if (this.dataset.available === 'false') return inactive;

    if (root.dataset.preorderEmptyOnly === 'true') {
      const entry = this.#readInventory()[String(this.variantId)];
      if (entry === undefined || entry.managed !== true) return inactive;
      if (Number(entry.quantity) > 0) return inactive;
    }

    return {
      active: true,
      key: root.dataset.preorderKey ?? '',
      message: root.dataset.preorderMessage ?? '',
    };
  }

  #readInventory() {
    if (this.#inventory !== null) return this.#inventory;

    this.#inventory = {};
    const data = this.querySelector('[data-ref="inventoryData"]');
    if (data === null) return this.#inventory;

    try {
      const parsed = JSON.parse(data.textContent);
      if (parsed !== null && typeof parsed === 'object') this.#inventory = parsed;
    } catch {
      /* map vide : la précommande « stock épuisé seulement » reste inactive */
    }

    return this.#inventory;
  }

  /** Synchronise la propriété de ligne (sans JS : input rendu par le Liquid). */
  #togglePreorderUi(preorder, available) {
    const property = this.querySelector('[data-ref="preorderProperty"]');
    if (property !== null) property.disabled = preorder.active === false;

    const note = this.querySelector('[data-ref="preorderNote"]');
    if (note !== null) note.hidden = preorder.active === false || available === false;
  }

  /* ---------- états ---------- */

  #setBusy(state) {
    this.#busy = state;
    this.setAttribute('aria-busy', String(state));

    if (this.#button === null) return;

    this.#button.setAttribute('aria-busy', String(state));
    this.#button.disabled = state || this.dataset.available === 'false';
  }

  #setStatus(message) {
    const target = this.querySelector('[data-ref="formStatus"]');
    if (target !== null) target.textContent = message;
  }

  #showError(message) {
    const target = this.querySelector('[data-ref="formError"]');
    if (target === null) return;

    target.textContent = message;
    target.hidden = message === '';
  }

  #clearError() {
    this.#showError('');
  }
}

if (customElements.get('tr-product-form') === undefined) {
  customElements.define('tr-product-form', TrProductForm);
}
